import { createPinia } from 'pinia';
import { createApp, createSSRApp, type App as VueApp } from 'vue';
import { createMemoryHistory, createRouter, createWebHistory, type Router } from 'vue-router';
import type { VueHeadClient } from '@unhead/vue';
import App from './App.vue';
import { createAppI18n, type AppI18n } from './i18n';
import { buildRoutes } from './router/routes';
import './styles/main.css';

export interface CreateOptions {
  ssr: boolean;
  hydrate: boolean;
  head: VueHeadClient;
}

export interface PokerApp {
  app: VueApp;
  router: Router;
  i18n: AppI18n;
}

export function createPokerApp(options: CreateOptions): PokerApp {
  const app = options.hydrate || options.ssr ? createSSRApp(App) : createApp(App);
  const router = createRouter({
    history: options.ssr ? createMemoryHistory() : createWebHistory(),
    routes: buildRoutes(),
    scrollBehavior(to, from, saved) {
      if (saved) return saved;
      if (to.hash) return { el: to.hash, behavior: 'smooth', top: 72 };
      if (to.path === from.path) return false;
      return { top: 0 };
    },
  });
  const i18n = createAppI18n();

  router.beforeEach((to) => {
    if (to.meta.locale) i18n.global.locale.value = to.meta.locale;
  });

  app.use(createPinia());
  app.use(router);
  app.use(i18n);
  app.use(options.head);

  return { app, router, i18n };
}
